/**
 * Een gecodeerde polylijn uitpakken naar punten.
 *
 * ------------------------------------------------------------------
 * Het algoritme van Google, en voor de derde keer in dit project: dezelfde
 * dertig regels staan in `backend/services/routing_service.py` (als encoder én
 * decoder) en in `android/.../data/Polylijn.kt`. Drie kopieën is geen
 * nalatigheid maar de prijs van drie talen; wat ze gelijk houdt is dat ze alle
 * drie tegen dezelfde string van buiten getoetst worden — de voorbeeldlijn uit
 * Google's eigen polylijndocumentatie — en niet tegen elkaar. Een encoder en
 * een decoder die dezelfde fout maken, bevestigen elkaar vrolijk.
 *
 * **De precisie is de hele val.** De server codeert op vijf decimalen en zet
 * dat getal in het antwoord (`vorm_decimalen`); Valhalla, die de lijn
 * aanlevert, codeert op zes. Pak je een lijn van zes decimalen uit op vijf —
 * of omgekeerd — dan komt er geen fout en geen lege lijst. Er komt een lijn met
 * het goede aantal punten en de goede vorm, een factor tien verkeerd, die op
 * het goede punt begint en de Noordzee in loopt. Precies het soort stilte dat
 * niemand meldt.
 *
 * Vandaar twee dingen: het aantal decimalen is een argument zonder stille
 * standaard, en [binnenNederland] staat erachter als vangrail. Niets tekenen is
 * beter dan geloofwaardig verkeerd tekenen.
 */

/** Waar de lijn hoort te liggen. Alles erbuiten is een precisiefout. */
export const GRENZEN = { latMin: 50.0, latMax: 54.0, lonMin: 3.0, lonMax: 7.5 };

/**
 * De punten in de lijn, als `[[lat, lon], ...]` — de vorm die react-leaflet
 * voor `<Polyline positions>` wil.
 *
 * Geeft een lege lijst bij een lege string, bij een onzinnig aantal decimalen,
 * of bij iets dat geen string is. Een afgekapte lijn geeft terug wat er wél
 * uit kwam, want buiten de string lezen is erger dan een punt missen.
 */
export const decodeer = (vorm, decimalen) => {
  if (typeof vorm !== "string" || vorm.length === 0) return [];
  if (!Number.isInteger(decimalen) || decimalen < 1 || decimalen > 9) return [];

  const deler = Math.pow(10, decimalen);
  const punten = [];
  let index = 0;
  let lat = 0;
  let lon = 0;

  while (index < vorm.length) {
    let verschuiving = 0;
    let resultaat = 0;
    let teken;
    do {
      if (index >= vorm.length) return punten;
      teken = vorm.charCodeAt(index++) - 63;
      resultaat |= (teken & 0x1f) << verschuiving;
      verschuiving += 5;
    } while (teken >= 0x20);
    lat += (resultaat & 1) !== 0 ? ~(resultaat >> 1) : resultaat >> 1;

    verschuiving = 0;
    resultaat = 0;
    do {
      if (index >= vorm.length) return punten;
      teken = vorm.charCodeAt(index++) - 63;
      resultaat |= (teken & 0x1f) << verschuiving;
      verschuiving += 5;
    } while (teken >= 0x20);
    lon += (resultaat & 1) !== 0 ? ~(resultaat >> 1) : resultaat >> 1;

    punten.push([lat / deler, lon / deler]);
  }
  return punten;
};

/**
 * Of deze lijn ergens kan liggen waar wij wegen hebben.
 *
 * De vangrail achter de precisie, en identiek aan `Polylijn.binnenNederland`
 * in de Android-app. Een lege lijst is niet "binnen Nederland": dan valt er
 * niets te tekenen en moet de aanroeper dezelfde kant op als bij een foute
 * lijn.
 */
export const binnenNederland = (punten) =>
  Array.isArray(punten) && punten.length > 0 && punten.every(
    (p) => Array.isArray(p) && p.length >= 2 &&
      p[0] >= GRENZEN.latMin && p[0] <= GRENZEN.latMax &&
      p[1] >= GRENZEN.lonMin && p[1] <= GRENZEN.lonMax);

/**
 * De lijn uit een route-antwoord, of een lege lijst.
 *
 * Eén plek waar de twee stappen samenkomen, zodat geen enkele aanroeper de
 * vangrail kan vergeten. `vorm_decimalen` komt uit het antwoord en wordt hier
 * niet aangenomen; ontbreekt het veld — een oudere server, een kapot antwoord —
 * dan is het `undefined`, valt de controle op gehele getallen, en komt er een
 * lege lijst uit in plaats van een gok.
 */
export const lijnUitRit = (rit) => {
  const punten = decodeer(rit && rit.vorm, rit && rit.vorm_decimalen);
  return binnenNederland(punten) ? punten : [];
};
